import type { CaptureSession } from '@/shared/capture/capture-session';
import {
  createIdempotencyKey,
  isPendingSyncQueueRecord,
  isRetryableSyncQueueRecord,
  isStaleSyncingRecord,
  readSessionLocationApiKey,
  STALE_SYNCING_RECORD_MS,
  sortSyncQueueRecordsNewestUpdateFirst,
  sortSyncQueueRecordsOldestFirst,
} from '@/shared/db/repositories/sync-queue-records';
import { type FieldDatabase, SYNC_QUEUE_STATUSES, type SyncQueueRecord } from '@/shared/db/schema';

export { createIdempotencyKey, STALE_SYNCING_RECORD_MS } from '@/shared/db/repositories/sync-queue-records';

export class SyncQueueRepository {
  constructor(private readonly database: FieldDatabase) {}

  async enqueueSession(session: CaptureSession, queuedAt: string) {
    const existing = await this.database.get('sync_queue', session.id);
    const locationApiKey = readSessionLocationApiKey(session);

    if (existing) {
      const updated: SyncQueueRecord = {
        ...existing,
        lastErrorCode: undefined,
        locationApiKey,
        locationId: session.locationId,
        status: SYNC_QUEUE_STATUSES.queued,
        updatedAt: queuedAt,
      };

      await this.database.put('sync_queue', updated);

      return updated;
    }

    const record: SyncQueueRecord = {
      id: session.id,
      sessionId: session.id,
      workflowType: session.workflowType,
      status: SYNC_QUEUE_STATUSES.queued,
      idempotencyKey: createIdempotencyKey(session),
      locationApiKey,
      locationId: session.locationId,
      createdAt: queuedAt,
      updatedAt: queuedAt,
    };

    await this.database.put('sync_queue', record);

    return record;
  }

  async listPending(input: { sessionIds?: Set<string> } = {}) {
    const records = await this.database.getAll('sync_queue');

    return sortSyncQueueRecordsOldestFirst(
      records.filter(record => isInSessionSet(record, input.sessionIds)).filter(isPendingSyncQueueRecord),
    );
  }

  async listRetryable(input: { sessionIds?: Set<string> } = {}) {
    const records = await this.database.getAll('sync_queue');

    return sortSyncQueueRecordsOldestFirst(
      records.filter(record => isInSessionSet(record, input.sessionIds)).filter(isRetryableSyncQueueRecord),
    );
  }

  async listAll() {
    const records = await this.database.getAll('sync_queue');

    return sortSyncQueueRecordsNewestUpdateFirst(records);
  }

  markSyncing(sessionId: string, updatedAt: string) {
    return this.patch(sessionId, { status: SYNC_QUEUE_STATUSES.syncing, updatedAt, lastErrorCode: undefined });
  }

  markSynced(sessionId: string, updatedAt: string) {
    return this.patch(sessionId, { status: SYNC_QUEUE_STATUSES.synced, updatedAt });
  }

  markFailed(sessionId: string, input: { updatedAt: string; errorCode: string }) {
    return this.patch(sessionId, {
      status: SYNC_QUEUE_STATUSES.syncFailed,
      updatedAt: input.updatedAt,
      lastErrorCode: input.errorCode,
    });
  }

  markConflict(sessionId: string, updatedAt: string, errorCode = 'CONFLICT') {
    return this.patch(sessionId, {
      status: SYNC_QUEUE_STATUSES.conflict,
      updatedAt,
      lastErrorCode: errorCode,
    });
  }

  markNeedsReview(sessionId: string, input: { updatedAt: string; errorCode: string }) {
    return this.patch(sessionId, {
      status: SYNC_QUEUE_STATUSES.needsReview,
      updatedAt: input.updatedAt,
      lastErrorCode: input.errorCode,
    });
  }

  retry(sessionId: string, updatedAt: string) {
    return this.patch(sessionId, {
      status: SYNC_QUEUE_STATUSES.queued,
      updatedAt,
      lastErrorCode: undefined,
    });
  }

  async retryAll(updatedAt: string, input: { sessionIds?: Set<string> } = {}) {
    const pending = await this.listRetryable({ sessionIds: input.sessionIds });

    return Promise.all(pending.map(record => this.retry(record.sessionId, updatedAt)));
  }

  async recoverStaleSyncing(updatedAt: string, input: { sessionIds?: Set<string>; staleAfterMs?: number } = {}) {
    const records = await this.database.getAll('sync_queue');
    const nowTime = Date.parse(updatedAt);
    const recovered = records
      .filter(record => isInSessionSet(record, input.sessionIds))
      .filter(record => isStaleSyncingRecord(record, nowTime, input.staleAfterMs ?? STALE_SYNCING_RECORD_MS));

    await Promise.all(
      recovered.map(record =>
        this.markFailed(record.sessionId, {
          errorCode: 'SYNC_INTERRUPTED',
          updatedAt,
        }),
      ),
    );

    return recovered.length;
  }

  private async patch(sessionId: string, patch: Partial<SyncQueueRecord>) {
    const record = await this.database.get('sync_queue', sessionId);

    if (!record) {
      throw new Error(`Sync queue record not found for session ${sessionId}.`);
    }

    const updated = {
      ...record,
      ...patch,
    };

    await this.database.put('sync_queue', updated);

    return updated;
  }
}

function isInSessionSet(record: SyncQueueRecord, sessionIds: Set<string> | undefined) {
  return !sessionIds || sessionIds.has(record.sessionId);
}
